import { t, getIntlLocale } from '../i18n/i18n';
/**
 * The "LEVEL 9" announcement — the on-screen half of the level-up celebration
 * (`design/gdd/character-creation.md`, amended 2026-08-25).
 *
 * Deliberately not a toast in the corner. A level-up is the rarest good thing
 * that happens in a run (roughly one every two runs at the current XP rates),
 * and the pillar of light on the character is already carrying the world side
 * of it; the text belongs in the middle of the screen where the player is
 * looking, and it must not need to be read to be understood.
 *
 * Positioned above centre rather than dead centre so it clears the character's
 * own head at the shoulder camera's framing, and `pointer-events-none`
 * throughout: this appears mid-fight, and a banner that swallowed a click
 * would cost the player a swing.
 */

/**
 * Held for a full second and a half before it starts to leave — longer than
 * `damage-numbers.ts`'s 1.1s floaters, because those are a running commentary
 * you glance at and this happens twice an hour. It outlasts the pillar's 1.8s
 * only slightly, so the two read as one event ending rather than two.
 */
const HOLD_MS = 1_500;
const FADE_MS = 450;
const DRIFT_PX = 18;
/** Two stacked shadows: a tight warm core and a wide soft halo. This is what makes the text read as gold rather than as yellow. */
const TEXT_GLOW = '0 0 12px rgba(255,214,120,0.85), 0 0 38px rgba(255,170,40,0.45)';

export class LevelUpBanner {
  private readonly root: HTMLDivElement;
  private readonly levelLine: HTMLDivElement;
  private readonly rewardLine: HTMLDivElement;
  private readonly progressLine: HTMLDivElement;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly reducedMotion: boolean;

  constructor(container: HTMLElement) {
    this.reducedMotion = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    this.root = document.createElement('div');
    this.root.className = [
      'fixed', 'left-1/2', '-translate-x-1/2', 'top-[22%]', 'z-[1250]',
      'pointer-events-none', 'select-none', 'text-center',
    ].join(' ');
    // Opacity is an inline style throughout rather than a starting utility
    // class the show/hide path then overrides — one mechanism owns visibility,
    // so the initial state and every later state are set the same way.
    this.root.style.opacity = '0';
    // Transition rather than a keyframe animation: the banner is shown and
    // hidden by opacity, and one property changing means nothing to cancel if
    // a second level lands while the first is still on screen.
    this.root.style.transition = this.reducedMotion
      ? `opacity ${FADE_MS}ms linear`
      : `opacity ${FADE_MS}ms ease-out, transform ${FADE_MS}ms ease-out`;

    const eyebrow = document.createElement('div');
    eyebrow.className = 'font-heading italic text-[1rem] text-amber-200/80';
    eyebrow.textContent = t('Level up');

    this.levelLine = document.createElement('div');
    this.levelLine.className = [
      'font-bold', 'text-amber-100', 'text-[clamp(2rem,7vw,3.5rem)]', 'leading-none', 'mt-1',
    ].join(' ');
    // The glow is what sells it as gold rather than as yellow text; text-shadow
    // rather than a filter so it costs nothing on a mid-range phone.
    this.levelLine.style.textShadow = TEXT_GLOW;

    /**
     * What the level actually *gives* you.
     *
     * `talentPointsAvailable(level) = 1 + (level - 1)`, so every level is
     * exactly one point — the reward is unconditional and worth naming. A
     * level-up that only showed a bigger number would be a notification; this
     * is the line that turns it into something the player goes and spends.
     */
    this.rewardLine = document.createElement('div');
    this.rewardLine.className = 'text-[0.85rem] text-amber-200/85 mt-1 font-medium';
    this.rewardLine.textContent = t('+1 talent point');

    this.progressLine = document.createElement('div');
    this.progressLine.className = 'text-[0.8rem] text-amber-100/60 mt-1.5 tabular-nums';

    this.root.append(eyebrow, this.levelLine, this.rewardLine, this.progressLine);
    container.appendChild(this.root);
  }

  /**
   * Shows the banner for `HOLD_MS`, then fades it.
   *
   * `progress` is optional because the two places a level-up can be announced
   * know different amounts: the room's mid-run broadcast carries the player's
   * exact XP standing, while an Arena round-end grant only knows the level.
   * Omitting it drops the line rather than printing a guess.
   */
  show(level: number, progress?: { into: number; needed: number }): void {
    this.levelLine.textContent = t('Level {n}', { n: level });
    this.progressLine.textContent = progress
      ? (progress.into >= progress.needed
        // At the cap there is no next level to be partway toward.
        ? t('Maximum level')
        : t('{into} / {needed} XP to level {next}', {
          into: progress.into.toLocaleString(getIntlLocale()),
          needed: progress.needed.toLocaleString(getIntlLocale()),
          next: level + 1,
        }))
      : '';

    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.root.style.opacity = '1';
    if (!this.reducedMotion) this.root.style.transform = 'translate(-50%, 0)';
    this.hideTimer = setTimeout(() => {
      this.root.style.opacity = '0';
      // Drifts up slightly as it leaves. Skipped under reduced motion, where
      // the fade alone carries it.
      if (!this.reducedMotion) this.root.style.transform = `translate(-50%, ${-DRIFT_PX}px)`;
    }, HOLD_MS);
  }

  dispose(): void {
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.root.remove();
  }
}
